import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  selectMissionCharacterImages,
  selectMissionDraft,
  selectMissionScript,
  selectIsMissionEditorMode,
} from '../../../redux/reducers/privateStateReducer/selectors';
import {selectPlayers} from '../../../redux/reducers/sharedStateReducer/selectors';
import {
  missionEditorAddCharacterImage,
  missionEditorUpdateDraft,
  missionScriptAppend,
  missionScriptRemove,
  missionShowReplica,
  setPlayerIsBot,
} from '../../../redux/actions';
import {
  createMissionMoment,
  MISSION_SCRIPT_KINDS,
  REPLICA_SIDES,
  REPLICA_SIDE_TITLES,
} from '../../../configs/campaigns/missionFormat';
import {
  MISSION_OBJECTIVE_PARAMS,
  MISSION_OBJECTIVE_TITLES,
  MISSION_OBJECTIVE_TYPES,
  MISSION_SPECIAL_BUILDING_OPTIONS,
  getMissionObjectiveText,
} from '../../../utils/missions/missionObjectives';
import {buildMissionConfig, downloadMissionConfig, getMissionScriptStats} from '../../../utils/missions/buildMissionConfig';
import store from '../../../redux/store';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';
import {useCollapseOnCornerPanelRequest} from '../../../hooks/visuals/useCornerPanels';
import './MissionEditorPanel.css';

const OBJECTIVE_TYPE_OPTIONS = Object.values(MISSION_OBJECTIVE_TYPES);

// Вкладки панели
const TABS = {
  REPLICA: 'replica',
  OBJECTIVE: 'objective',
  BOTS: 'bots',
  MISSION: 'mission',
};

const TAB_TITLES = {
  [TABS.REPLICA]: 'Реплика',
  [TABS.OBJECTIVE]: 'Задание',
  [TABS.BOTS]: 'Боты',
  [TABS.MISSION]: 'Миссия',
};

/**
 * ПАНЕЛЬ РЕДАКТОРА МИССИЙ.
 *
 * Стоит там же, где панель реакций в сетевых матчах, — слева над футером, и
 * разворачивается тем же приёмом (якорь 50x50 в потоке + absolute-разворот
 * вверх, плюс подъём z-index всего футера колбэком onExpandedChange).
 *
 * Всё, что автор делает через панель, попадает в ленту миссии ровно в том
 * месте, где он это сделал: при прохождении реплика/задание всплывут после
 * того же действия.
 */
const MissionEditorPanel = ({onExpandedChange, isCollapseRequested = false}) => {
  const dispatch = useDispatch();
  const isEditorMode = useSelector(selectIsMissionEditorMode);
  const draft = useSelector(selectMissionDraft);
  const script = useSelector(selectMissionScript);
  const characterImages = useSelector(selectMissionCharacterImages);
  const players = useSelector(selectPlayers);

  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.REPLICA);

  // Форма реплики
  const [replicaText, setReplicaText] = useState('');
  const [replicaName, setReplicaName] = useState('');
  const [replicaSide, setReplicaSide] = useState(REPLICA_SIDES.LEFT);
  const [selectedImageId, setSelectedImageId] = useState(null);

  // Форма задания
  const [objectiveType, setObjectiveType] = useState(MISSION_OBJECTIVE_TYPES.CAPTURE_CITY);
  const [objectiveParams, setObjectiveParams] = useState({});
  const [objectiveText, setObjectiveText] = useState('');
  const [isMainObjective, setIsMainObjective] = useState(true);
  const [isFailureObjective, setIsFailureObjective] = useState(false);

  // Развернули другую панель угла — редактор сворачивается
  useCollapseOnCornerPanelRequest(isCollapseRequested, isExpanded, () => {
    setIsExpanded(false);
    onExpandedChange?.(false);
  });

  if (!isEditorMode) {
    return null;
  }

  const setExpandedAndNotify = (value) => {
    setIsExpanded(value);
    onExpandedChange?.(value);
  };

  const appendEntry = (entry) => {
    const state = store.getState();
    dispatch(missionScriptAppend({
      id: `entry_${Date.now()}_${entry.kind}`,
      moment: createMissionMoment(state),
      ...entry,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const image = {
        id: `img_${Date.now()}`,
        name: file.name,
        // В конфиг уходит относительный путь; сам файл автор кладёт в public
        path: `/img/campaigns/${draft?.id || 'mission'}/${file.name}`,
        dataUrl: String(reader.result),
      };
      dispatch(missionEditorAddCharacterImage(image));
      setSelectedImageId(image.id);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleAddReplica = () => {
    if (!replicaText.trim()) return;
    const image = characterImages.find((item) => item.id === selectedImageId);

    const replica = {
      text: replicaText.trim(),
      characterName: replicaName.trim() || null,
      side: replicaSide,
      // Превью в редакторе — dataUrl, в конфиг уйдёт characterImagePath
      characterImage: image?.dataUrl || null,
      characterImagePath: image?.path || null,
    };

    appendEntry({kind: MISSION_SCRIPT_KINDS.REPLICA, replica});
    // Сразу показываем, чтобы автор увидел реплику глазами игрока
    dispatch(missionShowReplica(replica));
    setReplicaText('');
    MENU_SOUND_MANAGER.playMenuButtonClick();
  };

  const handleAddObjective = () => {
    const objective = {
      id: `objective_${Date.now()}`,
      type: objectiveType,
      params: objectiveParams,
      text: objectiveText.trim() || null,
      isMain: isMainObjective,
      isFailure: isFailureObjective,
    };

    appendEntry({kind: MISSION_SCRIPT_KINDS.OBJECTIVE, objective});
    setObjectiveText('');
    setObjectiveParams({});
    MENU_SOUND_MANAGER.playMenuButtonClick();
  };

  // «Дальше за этого игрока ходит ИИ»: помечаем игрока ботом (движок ботов
  // сразу подхватит его ход) и оставляем метку в ленте
  const handleBotAuto = (color) => {
    dispatch(setPlayerIsBot(color, true));
    appendEntry({kind: MISSION_SCRIPT_KINDS.BOT_AUTO, playerColor: color});
    MENU_SOUND_MANAGER.playMenuButtonClick();
  };

  const handleExport = () => {
    const mission = buildMissionConfig(store.getState().private.mission);
    downloadMissionConfig(mission);
  };

  const stats = getMissionScriptStats(script);
  const objectiveParamsSpec = MISSION_OBJECTIVE_PARAMS[objectiveType] || [];
  const playerColor = draft?.playerColor;

  const renderObjectiveParamInput = (param) => {
    const value = objectiveParams[param.name] ?? '';
    const onChange = (nextValue) => setObjectiveParams({...objectiveParams, [param.name]: nextValue});

    if (param.kind === 'color') {
      return (
        <select value={value} onChange={(event) => onChange(event.target.value)}>
          <option value={''}>—</option>
          {players.filter((player) => player.color !== playerColor).map((player) => (
            <option key={player.color} value={player.color}>{player.name}</option>
          ))}
        </select>
      );
    }
    if (param.kind === 'token') {
      return (
        <select value={value} onChange={(event) => onChange(event.target.value)}>
          <option value={''}>—</option>
          {MISSION_SPECIAL_BUILDING_OPTIONS.map((token) => (
            <option key={token} value={token}>{token}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        type={param.kind === 'number' ? 'number' : 'text'}
        value={value}
        placeholder={param.kind === 'hex' ? 'q,r' : ''}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  };

  return (
    <div className={`mission-editor-panel ${isExpanded ? '' : 'mission-editor-panel-collapsed'}`}>
      {!isExpanded && (
        <button
          type={'button'}
          className={'footer-heading-btn mission-editor-panel_toggle'}
          onClick={() => setExpandedAndNotify(true)}
          title={'Панель редактора миссий'}
        >
          <img src={'/img/icons/rules_white.png'} alt={'Редактор миссии'}/>
        </button>
      )}

      {isExpanded && (
        <div className={'mission-editor-panel-expanded standard-background'}>
          <div className={'mission-editor-panel_header'}>
            <span className={'mission-editor-panel_title'}>Панель редактора</span>
            <button
              type={'button'}
              className={'mission-editor-panel_close'}
              onClick={() => setExpandedAndNotify(false)}
            >
              ×
            </button>
          </div>

          <div className={'mission-editor-panel_tabs'}>
            {Object.values(TABS).map((tab) => (
              <button
                type={'button'}
                key={tab}
                className={`mission-editor-panel_tab ${activeTab === tab ? 'mission-editor-panel_tab--active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {TAB_TITLES[tab]}
              </button>
            ))}
          </div>

          <div className={'mission-editor-panel_body'}>
            {activeTab === TABS.REPLICA && (
              <>
                <label className={'mission-editor-panel_field'}>
                  <span>Имя персонажа</span>
                  <input value={replicaName} onChange={(event) => setReplicaName(event.target.value)}/>
                </label>

                <label className={'mission-editor-panel_field'}>
                  <span>Текст реплики</span>
                  <textarea
                    rows={3}
                    value={replicaText}
                    onChange={(event) => setReplicaText(event.target.value)}
                  />
                </label>

                <div className={'mission-editor-panel_field'}>
                  <span>Сторона</span>
                  <div className={'mission-editor-panel_row'}>
                    {Object.values(REPLICA_SIDES).map((side) => (
                      <button
                        type={'button'}
                        key={side}
                        className={`mission-editor-panel_chip ${replicaSide === side ? 'mission-editor-panel_chip--active' : ''}`}
                        onClick={() => setReplicaSide(side)}
                      >
                        {REPLICA_SIDE_TITLES[side]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={'mission-editor-panel_field'}>
                  <span>Персонаж</span>
                  <div className={'mission-editor-panel_images'}>
                    {characterImages.map((image) => (
                      <button
                        type={'button'}
                        key={image.id}
                        className={`mission-editor-panel_image ${selectedImageId === image.id ? 'mission-editor-panel_image--active' : ''}`}
                        onClick={() => setSelectedImageId(image.id)}
                        title={image.path}
                      >
                        <img src={image.dataUrl} alt={image.name}/>
                      </button>
                    ))}
                  </div>
                  <input type={'file'} accept={'image/*'} onChange={handleFileChange}/>
                </div>

                <button type={'button'} className={'mission-editor-panel_action'} onClick={handleAddReplica}>
                  Добавить реплику
                </button>
              </>
            )}

            {activeTab === TABS.OBJECTIVE && (
              <>
                <label className={'mission-editor-panel_field'}>
                  <span>Тип задания</span>
                  <select value={objectiveType} onChange={(event) => {
                    setObjectiveType(event.target.value);
                    setObjectiveParams({});
                  }}>
                    {OBJECTIVE_TYPE_OPTIONS.map((type) => (
                      <option key={type} value={type}>{MISSION_OBJECTIVE_TITLES[type]}</option>
                    ))}
                  </select>
                </label>

                {objectiveParamsSpec.map((param) => (
                  <label className={'mission-editor-panel_field'} key={param.name}>
                    <span>{param.label}</span>
                    {renderObjectiveParamInput(param)}
                  </label>
                ))}

                <label className={'mission-editor-panel_field'}>
                  <span>Текст для игрока (необязательно)</span>
                  <input value={objectiveText} onChange={(event) => setObjectiveText(event.target.value)}/>
                </label>

                <div className={'mission-editor-panel_row'}>
                  <label className={'mission-editor-panel_checkbox'}>
                    <input
                      type={'checkbox'}
                      checked={isMainObjective}
                      onChange={(event) => setIsMainObjective(event.target.checked)}
                    />
                    <span>Главное</span>
                  </label>
                  <label className={'mission-editor-panel_checkbox'}>
                    <input
                      type={'checkbox'}
                      checked={isFailureObjective}
                      onChange={(event) => setIsFailureObjective(event.target.checked)}
                    />
                    <span>Провал миссии</span>
                  </label>
                </div>

                <button type={'button'} className={'mission-editor-panel_action'} onClick={handleAddObjective}>
                  Добавить задание
                </button>
              </>
            )}

            {activeTab === TABS.BOTS && (
              <>
                <p className={'mission-editor-panel_hint'}>
                  Пока тумблер выключен, за игрока ходишь ты, и каждое действие пишется в миссию.
                  После включения ходы делает система ботов — они тоже записываются.
                </p>
                {players.filter((player) => player.color !== playerColor).map((player) => (
                  <div className={'mission-editor-panel_bot-row'} key={player.color}>
                    <span style={{color: player.hexColor}}>{player.name}</span>
                    {player.isBot ? (
                      <span className={'mission-editor-panel_bot-on'}>ходит ИИ</span>
                    ) : (
                      <button
                        type={'button'}
                        className={'mission-editor-panel_action'}
                        onClick={() => handleBotAuto(player.color)}
                      >
                        Передать ИИ
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}

            {activeTab === TABS.MISSION && (
              <>
                <label className={'mission-editor-panel_field'}>
                  <span>Название</span>
                  <input
                    value={draft?.title || ''}
                    onChange={(event) => dispatch(missionEditorUpdateDraft({title: event.target.value}))}
                  />
                </label>

                <label className={'mission-editor-panel_field'}>
                  <span>Описание</span>
                  <textarea
                    rows={2}
                    value={draft?.description || ''}
                    onChange={(event) => dispatch(missionEditorUpdateDraft({description: event.target.value}))}
                  />
                </label>

                <label className={'mission-editor-panel_field'}>
                  <span>Раундов в миссии (пусто — без ограничения)</span>
                  <input
                    type={'number'}
                    value={draft?.finalRound ?? ''}
                    onChange={(event) => dispatch(missionEditorUpdateDraft({
                      finalRound: event.target.value === '' ? null : Number(event.target.value),
                    }))}
                  />
                </label>

                <div className={'mission-editor-panel_stats'}>
                  <div>Записано ходов: {stats[MISSION_SCRIPT_KINDS.ACTION] || 0}</div>
                  <div>Реплик: {stats[MISSION_SCRIPT_KINDS.REPLICA] || 0}</div>
                  <div>Заданий: {stats[MISSION_SCRIPT_KINDS.OBJECTIVE] || 0}</div>
                </div>

                <div className={'mission-editor-panel_timeline'}>
                  {script.filter((entry) => entry.kind !== MISSION_SCRIPT_KINDS.ACTION).map((entry) => (
                    <div className={'mission-editor-panel_timeline-item'} key={entry.id}>
                      <span>
                        {entry.kind === MISSION_SCRIPT_KINDS.REPLICA && `Реплика: ${entry.replica.text.slice(0, 40)}`}
                        {entry.kind === MISSION_SCRIPT_KINDS.OBJECTIVE && `Задание: ${getMissionObjectiveText(entry.objective)}`}
                        {entry.kind === MISSION_SCRIPT_KINDS.BOT_AUTO && `ИИ ведёт ${entry.playerColor}`}
                      </span>
                      <button
                        type={'button'}
                        className={'mission-editor-panel_remove'}
                        onClick={() => dispatch(missionScriptRemove(entry.id))}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                <button type={'button'} className={'mission-editor-panel_action'} onClick={handleExport}>
                  Скачать конфиг миссии
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionEditorPanel;
